// ==============================================
// Admin Devices Page
// ==============================================

import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageSize } from '../../hooks/usePageSize';
import { PageSizeSelect } from '../../components/ui/PageSizeSelect';
import {
  DevicePhoneMobileIcon,
  ServerIcon,
  PlusIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowPathIcon,
  ArchiveBoxIcon,
  PauseCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { Device, DeviceStatus } from '../../types/device';
import { formatRelativeTime } from '../../utils/formatters';
import { AdminDeviceSearchInput } from '../../components/admin/devices';

type SortField = 'name' | 'device_type' | 'status' | 'registration_code' | 'owner' | 'last_seen_at';
type SortDirection = 'asc' | 'desc';

function formatFullDateTime(dateStr: string): string {
  return new Intl.DateTimeFormat('en-NZ', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(dateStr));
}

function getDeviceSortValue(device: Device, field: SortField): string | number {
  switch (field) {
    case 'name':
      return (device.name || device.serial_number).toLowerCase();
    case 'device_type':
      return device.device_type;
    case 'status':
      return device.status;
    case 'registration_code':
      return device.registration_code || '';
    case 'owner':
      return (device.user?.name || device.user?.email || '').toLowerCase();
    case 'last_seen_at':
      return device.last_seen_at ? new Date(device.last_seen_at).getTime() : 0;
  }
}

function truncateSerial(serial: string): string {
  const lastDash = serial.lastIndexOf('-');
  if (lastDash > 3) {
    return serial.slice(0, 4) + '…' + serial.slice(lastDash);
  }
  return serial;
}

const statusColors: Record<DeviceStatus, string> = {
  unregistered: 'bg-slate-500/20 text-slate-400',
  pending: 'bg-yellow-500/20 text-yellow-400',
  active: 'bg-green-500/20 text-green-400',
  suspended: 'bg-orange-500/20 text-orange-400',
  revoked: 'bg-red-500/20 text-red-400',
};

const statusIcons: Record<DeviceStatus, React.ReactNode> = {
  unregistered: <ArchiveBoxIcon className="w-4 h-4" />,
  pending: <ClockIcon className="w-4 h-4" />,
  active: <CheckCircleIcon className="w-4 h-4" />,
  suspended: <PauseCircleIcon className="w-4 h-4" />,
  revoked: <XCircleIcon className="w-4 h-4" />,
};

export function AdminDevicesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerFilter = searchParams.get('owner') || '';
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = usePageSize('devices');
  const [statusFilter, setStatusFilter] = useState<string>('');
  // Which attention reason the list is filtered to. '' = not filtering by
  // attention, 'any' = every device with a reason, otherwise one reason.
  const [attentionFilter, setAttentionFilter] = useState<string>('');
  const [attentionOpen, setAttentionOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copyTimeout = useRef<ReturnType<typeof setTimeout>>();

  const copyToClipboard = useCallback((text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    clearTimeout(copyTimeout.current);
    copyTimeout.current = setTimeout(() => setCopiedId(null), 1500);
  }, []);

  const clearOwnerFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('owner');
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  // Reset to the first page whenever the page size or filters change.
  useEffect(() => {
    setPage(1);
  }, [pageSize, statusFilter, search, ownerFilter, attentionFilter]);

  // Fetch devices
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'devices', { page, pageSize, search, status: statusFilter, owner: ownerFilter }],
    queryFn: () =>
      api.adminGetDevices({
        limit: pageSize,
        page,
        status: statusFilter || undefined,
        search: search || undefined,
        owner: ownerFilter || undefined,
      }),
  });

  // Which devices need someone to look at them, and why.
  const { data: attention } = useQuery({
    queryKey: ['admin', 'devices', 'attention'],
    queryFn: () => api.adminGetDeviceAttention(),
    refetchInterval: 60000,
  });

  // The drill-down set. Only fetched once a reason is selected; the ids are
  // intersected with the paged list below so search and sorting keep working.
  const { data: attentionSet } = useQuery({
    queryKey: ['admin', 'devices', 'attention', 'devices', attentionFilter],
    queryFn: () => api.adminGetDevicesNeedingAttention(attentionFilter === 'any' ? undefined : attentionFilter),
    enabled: attentionFilter !== '',
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['admin', 'devices', 'stats'],
    queryFn: () => api.adminGetDeviceStats(),
  });

  // Update device status
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.adminUpdateDeviceStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
    },
  });

  // Unlink device
  const unlinkDeviceMutation = useMutation({
    mutationFn: (id: string) => api.adminUnlinkDevice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
      alert('Device unlinked successfully. A new registration code has been generated.');
    },
    onError: (error: any) => {
      alert(`Failed to unlink device: ${error.response?.data?.error || error.message}`);
    },
  });

  const handleStatusChange = (deviceId: string, newStatus: string) => {
    if (confirm(`Are you sure you want to change this device's status to ${newStatus}?`)) {
      updateStatusMutation.mutate({ id: deviceId, status: newStatus });
    }
  };

  const handleUnlinkDevice = (deviceId: string, deviceName: string) => {
    if (confirm(`Are you sure you want to unlink "${deviceName}"?\n\nThis will:\n- Revert the device to unregistered status\n- Clear all user data and tokens\n- Generate a new registration code\n- Remove all entitlements\n\nThe device will need to be registered again.`)) {
      unlinkDeviceMutation.mutate(deviceId);
    }
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  /** device id -> why it needs attention, for the badge on each row. */
  const attentionById = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const d of attentionSet?.devices ?? []) m.set(d.device_id, d.reasons ?? []);
    return m;
  }, [attentionSet?.devices]);

  const sortedDevices = useMemo(() => {
    let devices = data?.devices ?? [];
    if (attentionFilter) devices = devices.filter((d: { id: string }) => attentionById.has(d.id));
    if (!sortField) return devices;
    return [...devices].sort((a, b) => {
      const aVal = getDeviceSortValue(a, sortField);
      const bVal = getDeviceSortValue(b, sortField);
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [data?.devices, sortField, sortDirection, attentionFilter, attentionById]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-white">Devices</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          Add Device
        </button>
      </div>

      {/* Needs attention — the one block that says something is WRONG.
          The status tiles below say only what a device IS. */}
      {attention && attention.total > 0 && (
        <div className="rounded-lg border border-amber-700/50 bg-amber-950/20">
          <button
            onClick={() => setAttentionOpen((o) => !o)}
            aria-expanded={attentionOpen}
            className="w-full flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-left hover:bg-amber-900/20 transition-colors rounded-lg"
          >
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-400 shrink-0" />
            <span className="text-2xl font-bold text-amber-300 tabular-nums">{attention.total}</span>
            <span className="text-[15px] text-amber-100">
              {attention.total === 1 ? 'Scout needs attention' : 'Scouts need attention'}
            </span>
            <span className="hidden sm:flex items-center gap-2 text-[13px] text-amber-200/70">
              {attention.by_reason
                .filter((r: { devices: number }) => r.devices > 0)
                .map((r: { reason: string; label: string; devices: number }) => (
                  <span key={r.reason}>{r.label} {r.devices}</span>
                ))
                .reduce((acc: React.ReactNode[], el: React.ReactNode, i: number) =>
                  i === 0 ? [el] : [...acc, <span key={`s${i}`} className="text-amber-200/30">·</span>, el], [])}
            </span>
            <span className="ml-auto text-[13px] text-amber-200/80">
              {attentionOpen ? 'Hide' : 'Show'} breakdown
            </span>
          </button>

          {attentionOpen && (
            <div className="border-t border-amber-800/40 px-2 pb-2">
              <button
                onClick={() => { setAttentionFilter('any'); setStatusFilter(''); setPage(1); }}
                className={`w-full flex items-center gap-3 px-3 py-2 mt-2 rounded text-left transition-colors ${
                  attentionFilter === 'any' ? 'bg-amber-800/40' : 'hover:bg-amber-900/30'
                }`}
              >
                <span className="w-10 text-right font-mono tabular-nums text-amber-200">{attention.total}</span>
                <span className="text-[15px] text-amber-50">All of them</span>
              </button>
              {attention.by_reason
                .filter((r: { devices: number }) => r.devices > 0)
                .map((r: { reason: string; label: string; detail: string; severity: string; devices: number }) => (
                  <button
                    key={r.reason}
                    onClick={() => { setAttentionFilter(r.reason); setStatusFilter(''); setPage(1); }}
                    className={`w-full flex items-start gap-3 px-3 py-2 rounded text-left transition-colors ${
                      attentionFilter === r.reason ? 'bg-amber-800/40' : 'hover:bg-amber-900/30'
                    }`}
                  >
                    <span className={`w-10 text-right font-mono tabular-nums ${
                      r.severity === 'critical' ? 'text-red-300' : 'text-amber-200'
                    }`}>{r.devices}</span>
                    <span>
                      <span className="text-[15px] text-amber-50">{r.label}</span>
                      <span className="block text-[13px] text-amber-200/60">{r.detail}</span>
                    </span>
                  </button>
                ))}
              {/* What was deliberately set aside. Shown, not hidden: an admin
                  should be able to see the judgement and disagree with it. */}
              {attention.excluded && (attention.excluded.internal > 0 || attention.excluded.abandoned_registration > 0) && (
                <p className="px-3 pt-2 pb-1 text-[13px] text-amber-200/50">
                  Not counted:{' '}
                  {attention.excluded.internal > 0 && (
                    <span title="Staff-owned, flagged as a dev unit, or with no owner attached">
                      {attention.excluded.internal} internal or dev
                    </span>
                  )}
                  {attention.excluded.internal > 0 && attention.excluded.abandoned_registration > 0 && ', '}
                  {attention.excluded.abandoned_registration > 0 && (
                    <span title="A registration that was never completed, whose owner already has a Scout reporting normally">
                      {attention.excluded.abandoned_registration} abandoned registration
                      {attention.excluded.abandoned_registration === 1 ? '' : 's'}
                    </span>
                  )}
                  .
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Active attention filter — say what the list is showing, and how to leave */}
      {attentionFilter && (
        <div className="flex items-center gap-3 text-[14px] text-slate-300">
          <span>
            Showing{' '}
            <strong className="text-amber-300">
              {attentionFilter === 'any'
                ? 'all Scouts needing attention'
                : attention?.by_reason?.find((r: { reason: string }) => r.reason === attentionFilter)?.label ?? attentionFilter}
            </strong>
            {attentionSet?.total != null && <span className="text-slate-400"> · {attentionSet.total} device{attentionSet.total === 1 ? '' : 's'}</span>}
          </span>
          <button
            onClick={() => { setAttentionFilter(''); setPage(1); }}
            className="px-2 py-0.5 rounded border border-slate-600 hover:border-slate-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Status Cards — lifecycle order */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
        <StatCard
          label="Total"
          value={stats?.total || 0}
          icon={<DevicePhoneMobileIcon className="w-4 h-4" />}
          onClick={() => { setStatusFilter(''); setPage(1); }}
          active={statusFilter === ''}
        />
        <StatCard
          label="Unregistered"
          value={stats ? stats.total - stats.active - stats.pending - stats.suspended - stats.revoked : 0}
          icon={<ArchiveBoxIcon className="w-4 h-4" />}
          color="text-slate-400"
          onClick={() => { setStatusFilter('unregistered'); setPage(1); }}
          active={statusFilter === 'unregistered'}
        />
        <StatCard
          label="Pending"
          value={stats?.pending || 0}
          icon={<ClockIcon className="w-4 h-4" />}
          color="text-yellow-400"
          onClick={() => { setStatusFilter('pending'); setPage(1); }}
          active={statusFilter === 'pending'}
        />
        <StatCard
          label="Active"
          value={stats?.active || 0}
          icon={<CheckCircleIcon className="w-4 h-4" />}
          color="text-green-400"
          onClick={() => { setStatusFilter('active'); setPage(1); }}
          active={statusFilter === 'active'}
        />
        <StatCard
          label="Suspended"
          value={stats?.suspended || 0}
          icon={<PauseCircleIcon className="w-4 h-4" />}
          color="text-orange-400"
          onClick={() => { setStatusFilter('suspended'); setPage(1); }}
          active={statusFilter === 'suspended'}
        />
        <StatCard
          label="Revoked"
          value={stats?.revoked || 0}
          icon={<XCircleIcon className="w-4 h-4" />}
          color="text-red-400"
          onClick={() => { setStatusFilter('revoked'); setPage(1); }}
          active={statusFilter === 'revoked'}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <AdminDeviceSearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by device ID, serial, name, owner, or MAC in metadata…"
        />

        {ownerFilter && (
          <div className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-sm">
            <span className="text-cyan-300">
              Owner: <span className="font-mono text-cyan-200">{ownerFilter}</span>
            </span>
            <button
              type="button"
              onClick={clearOwnerFilter}
              className="text-cyan-400 hover:text-white transition-colors"
              aria-label="Clear owner filter"
            >
              ×
            </button>
          </div>
        )}

        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Status</option>
          <option value="unregistered">Unregistered</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="revoked">Revoked</option>
        </select>

        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] })}
          className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700"
          title="Refresh"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Devices Table */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-700 bg-slate-800/50">
                  <SortableHeader field="name" label="Device" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="device_type" label="Type" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="status" label="Status" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="registration_code" label="Code" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="owner" label="Owner" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <SortableHeader field="last_seen_at" label="Last Seen" sortField={sortField} sortDirection={sortDirection} onSort={toggleSort} />
                  <th className="px-4 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {sortedDevices.map((device) => (
                  <tr
                    key={device.id}
                    className="hover:bg-slate-800/30 cursor-pointer"
                    onClick={() => navigate(`/admin/devices/${device.id}`)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-md bg-slate-700 flex items-center justify-center shrink-0">
                          {device.device_type === 'scout' ? (
                            <DevicePhoneMobileIcon className="w-4 h-4 text-cyan-400" />
                          ) : (
                            <ServerIcon className="w-4 h-4 text-purple-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {device.name || truncateSerial(device.serial_number)}
                          </p>
                          {/* Why this Scout is in the filtered list. Without it
                              the drill-down says which devices but not what is
                              wrong with them, which is the actual question. */}
                          {attentionFilter && attentionById.get(device.id)?.length ? (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {attentionById.get(device.id)!.map((r) => {
                                const meta = attention?.by_reason?.find((x: { reason: string }) => x.reason === r);
                                return (
                                  <span
                                    key={r}
                                    title={meta?.detail ?? r}
                                    className={`text-[11px] px-1.5 py-px rounded ${
                                      meta?.severity === 'critical'
                                        ? 'bg-red-900/40 text-red-300'
                                        : 'bg-amber-900/40 text-amber-200'
                                    }`}
                                  >
                                    {meta?.label ?? r}
                                  </span>
                                );
                              })}
                            </div>
                          ) : null}
                          <div className="flex items-center gap-1">
                            <code
                              className="text-xs text-slate-500 font-mono"
                              title={device.serial_number}
                            >
                              {truncateSerial(device.serial_number)}
                            </code>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(device.serial_number, `serial-${device.id}`);
                              }}
                              className="p-0.5 text-slate-500 hover:text-cyan-400 transition-colors"
                              title="Copy serial number"
                            >
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                            {copiedId === `serial-${device.id}` && (
                              <span className="text-[10px] text-cyan-400 font-medium animate-pulse">Copied!</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-slate-300">
                        {device.device_type === 'scout' ? 'Scout' : 'Hub'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded ${
                          statusColors[device.status]
                        }`}
                      >
                        {statusIcons[device.status]}
                        {device.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      {device.registration_code ? (
                        <div className="flex items-center gap-1">
                          <code className={`text-xs font-mono px-1.5 py-0.5 rounded ${
                            device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date()
                              ? 'text-red-400 bg-red-500/10'
                              : 'text-cyan-400 bg-cyan-500/10'
                          }`}>
                            {device.registration_code}
                          </code>
                          {device.registration_code_expires_at && new Date(device.registration_code_expires_at) < new Date() && (
                            <span className="text-[10px] text-red-400 font-medium">Expired</span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(device.registration_code!, `code-${device.id}`);
                            }}
                            className="p-0.5 text-slate-500 hover:text-cyan-400 transition-colors"
                            title="Copy code"
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                          {copiedId === `code-${device.id}` && (
                            <span className="text-[10px] text-cyan-400 font-medium animate-pulse">Copied!</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {device.user ? (
                        <div>
                          <p className="text-sm text-white">{device.user.name || 'Unnamed'}</p>
                          <p className="text-xs text-slate-400">{device.user.email}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">Unassigned</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-2.5 text-xs text-slate-400"
                      title={device.last_seen_at ? formatFullDateTime(device.last_seen_at) : undefined}
                    >
                      {device.last_seen_at
                        ? formatRelativeTime(device.last_seen_at)
                        : 'Never'}
                    </td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <select
                          value={device.status}
                          onChange={(e) => handleStatusChange(device.id, e.target.value)}
                          className="px-1.5 py-0.5 text-xs bg-slate-700 border border-slate-600 rounded text-white focus:outline-none"
                          disabled={updateStatusMutation.isPending}
                        >
                          <option value="unregistered">Unregistered</option>
                          <option value="pending">Pending</option>
                          <option value="active">Active</option>
                          <option value="suspended">Suspended</option>
                          <option value="revoked">Revoked</option>
                        </select>
                        {device.status !== 'unregistered' && (
                          <button
                            onClick={() => handleUnlinkDevice(device.id, device.name || device.serial_number)}
                            disabled={unlinkDeviceMutation.isPending}
                            className="px-1.5 py-0.5 text-xs bg-red-600/20 hover:bg-red-600/30 text-red-400 border border-red-600/50 rounded transition-colors disabled:opacity-50"
                            title="Unlink device and generate new registration code"
                          >
                            Unlink
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {sortedDevices.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-slate-400">
                      No devices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination + page size */}
            {data && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-700">
                <div className="flex items-center gap-4">
                  <PageSizeSelect value={pageSize} onChange={setPageSize} />
                  <p className="text-xs text-slate-400">
                    {data.total === 0
                      ? 'No devices'
                      : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, data.total)} of ${data.total}`}
                  </p>
                </div>
                {data.total > pageSize && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-white">
                      Page {page} of {Math.ceil(data.total / pageSize)}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= Math.ceil(data.total / pageSize)}
                      className="p-2 text-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Device Modal */}
      {showCreateModal && (
        <CreateDeviceModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  color = 'text-white',
  onClick,
  active = false,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <div
      className={`bg-slate-800/50 rounded-lg border px-3 py-2 transition-colors ${
        onClick ? 'cursor-pointer hover:border-cyan-500/60' : ''
      } ${active ? 'border-cyan-500 ring-1 ring-cyan-500/30' : 'border-slate-700'}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <span className="text-slate-400">{icon}</span>
        <span className={`text-lg font-bold ${color}`}>{value}</span>
      </div>
      <p className="text-xs text-slate-400 mt-0.5">{label}</p>
    </div>
  );
}

function SortableHeader({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
}: {
  field: SortField;
  label: string;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}) {
  const isActive = sortField === field;
  const Icon = isActive
    ? sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon
    : ChevronUpDownIcon;

  return (
    <th
      className="px-4 py-2 font-medium cursor-pointer select-none hover:text-slate-200 transition-colors"
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
      </span>
    </th>
  );
}

function CreateDeviceModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    serial_number: '',
    model_code: '',
    device_type: 'scout' as 'scout' | 'command_hub',
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.adminCreateDevice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'devices'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-white mb-4">Add Device to Inventory</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Serial Number *
            </label>
            <input
              type="text"
              value={formData.serial_number}
              onChange={(e) =>
                setFormData({ ...formData, serial_number: e.target.value })
              }
              required
              placeholder="VX-001234"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Model Code
            </label>
            <input
              type="text"
              value={formData.model_code}
              onChange={(e) =>
                setFormData({ ...formData, model_code: e.target.value })
              }
              placeholder="VX-SP-01"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:border-cyan-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-400 mb-1">
              Device Type *
            </label>
            <select
              value={formData.device_type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  device_type: e.target.value as 'scout' | 'command_hub',
                })
              }
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
            >
              <option value="scout">Scout</option>
              <option value="command_hub">Command Hub</option>
            </select>
          </div>

          {createMutation.isError && (
            <p className="text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error || 'Failed to create device'}
            </p>
          )}

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creating...' : 'Add Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminDevicesPage;
